// src/TickerMobileFocusFilterBar.js
import React from "react";
import { sortSourceListAlphabetically } from "../../utils/sourceModel";
import { toPersianDigits } from "../../utils/toPersianDigits";
import RetryLogoImage from "../media/RetryLogoImage";
import {
  FOCUS_PAGER_PROGRESS_EVENT,
} from "../../utils/focusPagerProgress";
import "./TickerMobileFocusFilterBar.css";

const FOCUS_ICON_FOLDERS = {
  day: "source-icons-focus-day",
  night: "source-icons-focus-night",
};

/*
|--------------------------------------------------------------------------
| Decoded focus-logo memory cache
|--------------------------------------------------------------------------
|
| Cache Storage prevents another network download, but the browser still has
| to read and decode the PNG before it can paint it. Keeping these Image
| objects alive moves that work ahead of the user's Focus Mode interaction.
|
*/
const decodedFocusLogoImages = new Map();

const getPreferredFocusIconMode = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "day";

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "night"
    : "day";
};

const getFocusIconSrc = (faviconSrc, mode) => {
  if (!faviconSrc) return "";

  const folder = FOCUS_ICON_FOLDERS[mode] || FOCUS_ICON_FOLDERS.day;

  if (faviconSrc.includes("source-icons300px")) {
    return faviconSrc.replace("source-icons300px", folder);
  }

  if (faviconSrc.includes("source-icons-black-white")) {
    return faviconSrc.replace("source-icons-black-white", folder);
  }

  const cleanSrc = faviconSrc.split("?")[0].split("#")[0];
  const fileName = cleanSrc.split("/").pop();

  if (!fileName) return faviconSrc;

  if (faviconSrc.startsWith("/")) {
    return `/${folder}/${fileName}`;
  }

  if (faviconSrc.startsWith("./")) {
    return `./${folder}/${fileName}`;
  }

  return `/${folder}/${fileName}`;
};

const loadAndDecodeFocusLogo = (src) => {
  if (!src || typeof Image === "undefined") {
    return Promise.resolve();
  }

  const cachedEntry = decodedFocusLogoImages.get(src);

  if (cachedEntry) {
    return cachedEntry.promise;
  }

  const image = new Image();

  const promise = new Promise((resolve) => {
    let hasSettled = false;

    const settle = () => {
      if (hasSettled) return;

      hasSettled = true;
      resolve();
    };

    const decodeAndSettle = () => {
      if (typeof image.decode !== "function") {
        settle();
        return;
      }

      image
        .decode()
        .catch(() => {
          /*
           * A decode rejection must not block the rest of the logo queue.
           * The mounted <img> still keeps its normal error fallback.
           */
        })
        .finally(settle);
    };

    image.onload = decodeAndSettle;
    image.onerror = settle;
    image.src = src;

    if (image.complete && image.naturalWidth > 0) {
      decodeAndSettle();
    }
  });

  decodedFocusLogoImages.set(src, {
    image,
    promise,
  });

  return promise;
};

const scheduleFocusLogoPreload = (callback) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  if (typeof window.requestIdleCallback === "function") {
    const idleCallbackId = window.requestIdleCallback(callback, {
      timeout: 800,
    });

    return () => {
      window.cancelIdleCallback?.(idleCallbackId);
    };
  }

  const timeoutId = window.setTimeout(callback, 0);

  return () => {
    window.clearTimeout(timeoutId);
  };
};

const clamp01 = (value) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) return 0;

  return Math.max(0, Math.min(numeric, 1));
};

/*
|--------------------------------------------------------------------------
| Restored Focus capsule spacing
|--------------------------------------------------------------------------
|
| Keep the source content on the exact same pixels in base/active layers so
| the curved color transition never creates a double image or a settle jump.
| The OLD active-capsule spacing is restored on the BLACK PILL GEOMETRY
| itself instead of moving the active logo/text layer:
|
|   active padding: 4px 4px 4px 10px
|                   top right bottom left
|
| This is the single source of truth used for pill width, pill position and
| ticker centering. The existing item/list padding and gaps stay untouched.
|--------------------------------------------------------------------------
*/
const ACTIVE_PILL_PADDING = Object.freeze({
  top: 4,
  right: 4,
  bottom: 4,
  left: 10,
});


const buildRoundedCapsuleClipPath = ({
  x,
  y,
  width,
  height,
  radius,
  cornerSegments = 6,
}) => {
  const safeWidth = Math.max(0, Number(width) || 0);
  const safeHeight = Math.max(0, Number(height) || 0);
  const safeRadius = Math.max(
    0,
    Math.min(
      Number(radius) || 0,
      safeWidth / 2,
      safeHeight / 2
    )
  );

  if (safeWidth <= 0 || safeHeight <= 0) {
    return "inset(0 100% 0 0)";
  }

  const points = [];

  const addArc = (centerX, centerY, startAngle, endAngle) => {
    for (let index = 0; index <= cornerSegments; index += 1) {
      const ratio = index / cornerSegments;
      const angle = startAngle + (endAngle - startAngle) * ratio;
      const pointX = centerX + Math.cos(angle) * safeRadius;
      const pointY = centerY + Math.sin(angle) * safeRadius;

      points.push(`${pointX.toFixed(3)}px ${pointY.toFixed(3)}px`);
    }
  };

  /*
  |------------------------------------------------------------------------
  | Exact pill silhouette
  |------------------------------------------------------------------------
  |
  | The active source layer is clipped by the same rounded rectangle that is
  | physically painted as the black pill. Because the polygon is expressed in
  | each button's local coordinate system, any part of the pill that sits
  | outside that button is naturally clipped by the button box, while the only
  | visible color-change boundary is the real curved pill edge.
  |
  | This avoids the old `inset(...)` behavior whose edge was always a straight
  | vertical line, even when the black capsule itself was rounded.
  |------------------------------------------------------------------------
  */
  addArc(
    x + safeWidth - safeRadius,
    y + safeRadius,
    -Math.PI / 2,
    0
  );
  addArc(
    x + safeWidth - safeRadius,
    y + safeHeight - safeRadius,
    0,
    Math.PI / 2
  );
  addArc(
    x + safeRadius,
    y + safeHeight - safeRadius,
    Math.PI / 2,
    Math.PI
  );
  addArc(
    x + safeRadius,
    y + safeRadius,
    Math.PI,
    (Math.PI * 3) / 2
  );

  return `polygon(${points.join(", ")})`;
};

const setActiveContentClipPath = (element, clipPath) => {
  if (!element) return;

  element.style.clipPath = clipPath;
  element.style.webkitClipPath = clipPath;
};

const TickerMobileFocusFilterBar = ({
  focusSource,
  activeSources = [],
  favicons = {},
  onSourceSelect,
  isActive = false,
}) => {
  const listRef = React.useRef(null);
  const pillRef = React.useRef(null);
  const buttonRefs = React.useRef({});
  const activeContentRefs = React.useRef({});
  const currentMotionRef = React.useRef(null);
  const measureRafRef = React.useRef(null);
  const geometryRef = React.useRef({
    listWidth: 0,
    sourceTargets: new Map(),
  });
  const visibleActiveSourcesRef = React.useRef(new Set());

  const alphabeticallySortedSources = React.useMemo(() => {
    return sortSourceListAlphabetically(activeSources);
  }, [activeSources]);

  const [focusIconMode, setFocusIconMode] = React.useState(
    getPreferredFocusIconMode
  );

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const updateFocusIconMode = (event) => {
      setFocusIconMode(event.matches ? "night" : "day");
    };

    setFocusIconMode(mediaQuery.matches ? "night" : "day");

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateFocusIconMode);

      return () => {
        mediaQuery.removeEventListener("change", updateFocusIconMode);
      };
    }

    mediaQuery.addListener(updateFocusIconMode);

    return () => {
      mediaQuery.removeListener(updateFocusIconMode);
    };
  }, []);

  React.useEffect(() => {
    const logoUrls = alphabeticallySortedSources.flatMap((source) => {
      const faviconSrc = favicons[source];
      const focusIconSrc = getFocusIconSrc(
        faviconSrc,
        focusIconMode
      );

      return [focusIconSrc, faviconSrc].filter(Boolean);
    });

    const uniqueLogoUrls = [...new Set(logoUrls)];

    return scheduleFocusLogoPreload(() => {
      uniqueLogoUrls.forEach((logoUrl) => {
        loadAndDecodeFocusLogo(logoUrl);
      });
    });
  }, [
    alphabeticallySortedSources,
    favicons,
    focusIconMode,
  ]);

  const clearAllActiveContent = React.useCallback(() => {
    Object.values(activeContentRefs.current).forEach((content) => {
      if (!content) return;

      content.style.opacity = "0";
      setActiveContentClipPath(
        content,
        "inset(0 100% 0 0)"
      );
    });

    visibleActiveSourcesRef.current = new Set();
  }, []);

  const measureTickerGeometry = React.useCallback(() => {
    const list = listRef.current;

    if (!list) return false;

    const listRect = list.getBoundingClientRect();
    const sourceTargets = new Map();

    alphabeticallySortedSources.forEach((source) => {
      const button = buttonRefs.current[source];

      if (!button) return;

      const buttonRect = button.getBoundingClientRect();
      const baseContentNodes = Array.from(button.children).filter((node) => {
        return (
          node.classList?.contains(
            "ticker-mobile-focus-filter__icon-wrap--base"
          ) ||
          (node.classList?.contains(
            "ticker-mobile-focus-filter__name"
          ) &&
            !node.classList?.contains(
              "ticker-mobile-focus-filter__name--active"
            ))
        );
      });

      let contentLeft = buttonRect.left;
      let contentRight = buttonRect.right;

      if (baseContentNodes.length) {
        const contentRects = baseContentNodes.map((node) => {
          return node.getBoundingClientRect();
        });

        contentLeft = Math.min(
          ...contentRects.map((rect) => rect.left)
        );
        contentRight = Math.max(
          ...contentRects.map((rect) => rect.right)
        );
      }

      const buttonX =
        buttonRect.left - listRect.left + list.scrollLeft;
      const pillX =
        contentLeft -
        ACTIVE_PILL_PADDING.left -
        listRect.left +
        list.scrollLeft;
      const pillWidth = Math.max(
        0,
        contentRight +
          ACTIVE_PILL_PADDING.right -
          (contentLeft - ACTIVE_PILL_PADDING.left)
      );

      sourceTargets.set(source, {
        buttonX,
        buttonWidth: buttonRect.width,
        buttonHeight: buttonRect.height,
        pillX,
        pillWidth,
        pillCenter: pillX + pillWidth / 2,
      });
    });

    geometryRef.current = {
      listWidth: list.clientWidth || listRect.width,
      sourceTargets,
    };

    return sourceTargets.size > 0;
  }, [alphabeticallySortedSources]);

  const syncActiveContentToGeometry = React.useCallback(
    (pillX, pillWidth) => {
      const geometry = geometryRef.current;
      const pillHeight = 28;
      const pillY = 0;
      const pillRadius = Math.min(pillHeight / 2, pillWidth / 2);
      const pillRight = pillX + pillWidth;
      const nextVisibleSources = new Set();

      geometry.sourceTargets.forEach((target, source) => {
        const activeContent = activeContentRefs.current[source];

        if (!activeContent) return;

        const buttonLeft = target.buttonX;
        const buttonRight = buttonLeft + target.buttonWidth;
        const hasOverlap =
          Math.min(buttonRight, pillRight) -
            Math.max(buttonLeft, pillX) >
          0.01;

        if (!hasOverlap) return;

        const localPillX = pillX - buttonLeft;
        const clipPath = buildRoundedCapsuleClipPath({
          x: localPillX,
          y: pillY,
          width: pillWidth,
          height: pillHeight,
          radius: pillRadius,
        });

        activeContent.style.opacity = "1";
        setActiveContentClipPath(activeContent, clipPath);
        nextVisibleSources.add(source);
      });

      visibleActiveSourcesRef.current.forEach((source) => {
        if (nextVisibleSources.has(source)) return;

        const activeContent = activeContentRefs.current[source];
        if (!activeContent) return;

        activeContent.style.opacity = "0";
        setActiveContentClipPath(
          activeContent,
          "inset(0 100% 0 0)"
        );
      });

      visibleActiveSourcesRef.current = nextVisibleSources;
    },
    []
  );

  const applyMotion = React.useCallback(
    (fromSource, toSource, rawProgress) => {
      const list = listRef.current;
      const pill = pillRef.current;

      if (!list || !pill) return;

      let geometry = geometryRef.current;
      let from = geometry.sourceTargets.get(fromSource);
      let to = geometry.sourceTargets.get(toSource) || from;

      if (!from || !to) {
        if (!measureTickerGeometry()) return;

        geometry = geometryRef.current;
        from = geometry.sourceTargets.get(fromSource);
        to = geometry.sourceTargets.get(toSource) || from;
      }

      if (!from || !to) return;

      const progress = clamp01(rawProgress);

      /*
      |--------------------------------------------------------------------
      | Circular wrap: snap instead of crossing the whole ticker
      |--------------------------------------------------------------------
      |
      | The pager uses cloned edge pages to create an infinite loop. A
      | transition between the last and first real source is therefore only
      | one page apart in the pager, even though those sources sit at opposite
      | ends of this finite ticker list. Interpolating their ticker geometry
      | would visibly sweep the pill across every source.
      |
      | Keep normal interpolation for every adjacent source pair. Only for the
      | first <-> last loop boundary, hold the current source until the swipe
      | passes 50%, then snap directly to the destination source.
      |--------------------------------------------------------------------
      */
      const firstSource = alphabeticallySortedSources[0];
      const lastSource =
        alphabeticallySortedSources[
          alphabeticallySortedSources.length - 1
        ];
      const isLoopBoundaryTransition =
        alphabeticallySortedSources.length > 1 &&
        ((fromSource === firstSource && toSource === lastSource) ||
          (fromSource === lastSource && toSource === firstSource));
      const motionFrom = isLoopBoundaryTransition
        ? progress < 0.5
          ? from
          : to
        : from;
      const motionTo = isLoopBoundaryTransition ? motionFrom : to;
      const motionProgress = isLoopBoundaryTransition ? 0 : progress;

      const sourceCenter =
        motionFrom.pillCenter +
        (motionTo.pillCenter - motionFrom.pillCenter) * motionProgress;
      /*
       * Keep the browser's native RTL scrollLeft semantics. Assigning the
       * desired logical position directly lets Safari/Chromium clamp at the
       * first/last source without introducing a second coordinate system.
       */
      const desiredScrollLeft =
        sourceCenter - geometry.listWidth / 2;

      /*
      |--------------------------------------------------------------------
      | Hot swipe path: no layout reads
      |--------------------------------------------------------------------
      |
      | Source/button geometry is measured only when layout actually changes.
      | During every swipe frame we interpolate cached numbers and perform only
      | compositor-friendly writes. This removes the old read -> scroll write ->
      | forced read -> mask loop that made the capsule look one frame behind.
      |--------------------------------------------------------------------
      */
      if (Math.abs(list.scrollLeft - desiredScrollLeft) > 0.01) {
        list.scrollLeft = desiredScrollLeft;
      }

      const x =
        motionFrom.pillX +
        (motionTo.pillX - motionFrom.pillX) * motionProgress;
      const width =
        motionFrom.pillWidth +
        (motionTo.pillWidth - motionFrom.pillWidth) * motionProgress;

      pill.style.width = `${Math.max(0, width)}px`;
      pill.style.transform = `translate3d(${x}px, 0, 0)`;
      pill.classList.add(
        "ticker-mobile-focus-filter__active-pill--ready"
      );

      syncActiveContentToGeometry(x, width);

      currentMotionRef.current = {
        fromSource,
        toSource,
        progress,
      };
    },
    [
      alphabeticallySortedSources,
      measureTickerGeometry,
      syncActiveContentToGeometry,
    ]
  );

  const applySettledSource = React.useCallback(
    (source) => {
      if (!source || !buttonRefs.current[source]) return;

      applyMotion(source, source, 0);
    },
    [applyMotion]
  );

  const scheduleCurrentMotion = React.useCallback(() => {
    if (typeof requestAnimationFrame === "undefined") {
      const motion = currentMotionRef.current;

      if (motion) {
        applyMotion(
          motion.fromSource,
          motion.toSource,
          motion.progress
        );
      } else {
        applySettledSource(focusSource);
      }
      return;
    }

    if (measureRafRef.current !== null) return;

    measureRafRef.current = requestAnimationFrame(() => {
      measureRafRef.current = null;
      const motion = currentMotionRef.current;

      if (motion) {
        applyMotion(
          motion.fromSource,
          motion.toSource,
          motion.progress
        );
      } else {
        applySettledSource(focusSource);
      }
    });
  }, [applyMotion, applySettledSource, focusSource]);

  React.useLayoutEffect(() => {
    if (!isActive || !alphabeticallySortedSources.length) {
      clearAllActiveContent();
      return undefined;
    }

    currentMotionRef.current = null;
    measureTickerGeometry();
    applySettledSource(focusSource);

    const list = listRef.current;
    const handleGeometryChange = () => {
      measureTickerGeometry();
      scheduleCurrentMotion();
    };
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(handleGeometryChange)
        : null;

    if (observer && list) {
      observer.observe(list);

      Object.values(buttonRefs.current).forEach((button) => {
        if (button) observer.observe(button);
      });
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", handleGeometryChange);
    }

    return () => {
      observer?.disconnect();

      if (!observer && typeof window !== "undefined") {
        window.removeEventListener("resize", handleGeometryChange);
      }

      if (
        measureRafRef.current !== null &&
        typeof cancelAnimationFrame !== "undefined"
      ) {
        cancelAnimationFrame(measureRafRef.current);
        measureRafRef.current = null;
      }
    };
  }, [
    alphabeticallySortedSources,
    applySettledSource,
    clearAllActiveContent,
    focusSource,
    isActive,
    measureTickerGeometry,
    scheduleCurrentMotion,
  ]);

  React.useEffect(() => {
    if (!isActive || typeof window === "undefined") {
      return undefined;
    }

    const handlePagerProgress = (event) => {
      const fromSource = event.detail?.fromSource;
      const toSource = event.detail?.toSource || fromSource;
      const progress = event.detail?.progress;

      if (!fromSource || !toSource) return;
      if (!buttonRefs.current[fromSource]) return;
      if (!buttonRefs.current[toSource]) return;

      applyMotion(fromSource, toSource, progress);
    };

    window.addEventListener(
      FOCUS_PAGER_PROGRESS_EVENT,
      handlePagerProgress
    );

    return () => {
      window.removeEventListener(
        FOCUS_PAGER_PROGRESS_EVENT,
        handlePagerProgress
      );
    };
  }, [applyMotion, isActive]);

  if (!alphabeticallySortedSources.length) return null;

  return (
    <div
      className={`ticker-mobile-focus-filter${
        isActive
          ? " ticker-mobile-focus-filter--visible"
          : " ticker-mobile-focus-filter--hidden"
      }`}
      aria-hidden={!isActive}
    >
      <div className="ticker-mobile-focus-filter__capsule">
        <div
          className="ticker-mobile-focus-filter__list"
          ref={listRef}
        >
          <span
            ref={pillRef}
            className="ticker-mobile-focus-filter__active-pill"
            aria-hidden="true"
          />

          {alphabeticallySortedSources.map((source) => {
            const isSelected = source === focusSource;
            const sourceName = toPersianDigits(source);
            const faviconSrc = favicons[source];
            const focusIconSrc = getFocusIconSrc(
              faviconSrc,
              focusIconMode
            );

            return (
              <button
                key={source}
                ref={(node) => {
                  if (node) {
                    buttonRefs.current[source] = node;
                  } else {
                    delete buttonRefs.current[source];
                  }
                }}
                type="button"
                className="ticker-mobile-focus-filter__item"
                onClick={() => onSourceSelect?.(source)}
                title={sourceName}
                aria-label={sourceName}
                aria-pressed={isSelected}
                tabIndex={isSelected ? 0 : -1}
              >
                <span className="ticker-mobile-focus-filter__icon-wrap ticker-mobile-focus-filter__icon-wrap--base">
                  {focusIconSrc || faviconSrc ? (
                    <RetryLogoImage
                      src={focusIconSrc || faviconSrc}
                      fallbackSrc={faviconSrc}
                      alt=""
                      className="ticker-mobile-focus-filter__icon"
                      fallbackText={source.charAt(0)}
                      loading="eager"
                      decoding="async"
                    />
                  ) : (
                    <span className="ticker-mobile-focus-filter__default-icon">
                      {source.charAt(0)}
                    </span>
                  )}
                </span>

                <span className="ticker-mobile-focus-filter__name">
                  <span className="ticker-mobile-focus-filter__name-visible">
                    {sourceName}
                  </span>
                  <span
                    className="ticker-mobile-focus-filter__name-sizer"
                    aria-hidden="true"
                  >
                    {sourceName}
                  </span>
                </span>

                <span
                  ref={(node) => {
                    if (node) {
                      activeContentRefs.current[source] = node;
                    } else {
                      delete activeContentRefs.current[source];
                    }
                  }}
                  className="ticker-mobile-focus-filter__active-content"
                  aria-hidden="true"
                >
                  <span className="ticker-mobile-focus-filter__icon-wrap ticker-mobile-focus-filter__icon-wrap--active">
                    {faviconSrc || focusIconSrc ? (
                      <RetryLogoImage
                        src={faviconSrc || focusIconSrc}
                        fallbackSrc={focusIconSrc}
                        alt=""
                        className="ticker-mobile-focus-filter__icon"
                        fallbackText={source.charAt(0)}
                        loading="eager"
                        decoding="async"
                      />
                    ) : (
                      <span className="ticker-mobile-focus-filter__default-icon">
                        {source.charAt(0)}
                      </span>
                    )}
                  </span>

                  <span className="ticker-mobile-focus-filter__name ticker-mobile-focus-filter__name--active">
                    <span className="ticker-mobile-focus-filter__name-visible">
                      {sourceName}
                    </span>
                    <span
                      className="ticker-mobile-focus-filter__name-sizer"
                      aria-hidden="true"
                    >
                      {sourceName}
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TickerMobileFocusFilterBar;
